import { AuthContext, ErrorContext, PriceBookContextModel } from 'Context';
import { CustomerOrderModel, Validate } from 'Models';
import { ERROR_CONTEXT_TYPES, ORDER_TYPES, QUOTE_STATUS, USER_ROLES } from 'common/enums';
import {
  checkRequiredFieldsForFinalize,
  checkRequiredFieldsForFinalizeLoc,
  finalizeBtnTooltipText,
} from 'common/check-required-for-finalize';
import CommonConditionsModel from 'Models/ToolbarButtons/CommonConditionsModel';

class ToolbarButtonModel extends CommonConditionsModel {
  constructor(higherOrderInstance) {
    super(higherOrderInstance);

    this.isActiveQuoteValidationRequested = false;
    this.asyncIsActiveQuote = this.asyncIsActiveQuote.bind(this);
  }

  get visible() {
    return true;
  }

  get disabled() {
    return this.appState.disabled;
  }

  get requiredFinalizeFields() {
    const moBean = this.controller.getMasterOrderBean();

    const masterOrderValidationResults = checkRequiredFieldsForFinalize(
      this.masterOrder,
      !this.controller.rollUpDisabled,
      this.controller.constantsModel.getRequiredForFinalize,
      this.appState.errors.addOnFinalizeAllow,
      true,
      this.controller.isQuoteContainUnderAllocatedItems()
    );

    if (!PriceBookContextModel.isSupportsCommUnity) {
      return masterOrderValidationResults;
    }

    let { message, fields, separateLineFields } = masterOrderValidationResults;
    const locations = this.masterOrder.locations;
    const isQuoteOverAllocated = CustomerOrderModel.isQuoteOverAllocated(moBean);

    for (let i = 0; i < locations.length; i++) {
      const locationErrors = checkRequiredFieldsForFinalizeLoc(
        locations[i],
        this.masterOrder,
        isQuoteOverAllocated,
        this.masterOrder.isSaveAllowed
      );

      const hasExplicitLocationErrors = locationErrors.fields.some(field => Object.getOwnPropertyNames(field).length);
      const hasAdditionalBlockingMessage =
        locationErrors.message && locationErrors.message.indexOf('@msg_validate_email_address_message@') !== -1;

      if (hasExplicitLocationErrors || hasAdditionalBlockingMessage) {
        separateLineFields.push({ fieldLabel: '@msg_resolve_location_errors@' });
        break;
      }
    }

    if (isQuoteOverAllocated) {
      separateLineFields.push({ fieldLabel: '@msg_locations_exceed_order_totals@' });
    }

    if (this.masterOrder.isSaveAllowed) {
      separateLineFields.push({ fieldLabel: '@msg_save_changes@' });
    }

    return { message, fields, separateLineFields };
  }

  get finalizeBtnDisabled() {
    return Boolean(
      this.requiredFinalizeFields.fields.length ||
      this.requiredFinalizeFields.separateLineFields.length ||
      [QUOTE_STATUS.FINALIZED, QUOTE_STATUS.SUBMITTED, QUOTE_STATUS.CLOSED].includes(this.masterOrder.quoteStatus) ||
      !this.appState.isSaved ||
      (!PriceBookContextModel.isSupportsCommUnity && !this.masterOrder.trackingLocationId) ||
      !this.controller.rollUpDisabled
    );
  }

  get unFinalizeBtnDisabled() {
    return Boolean(
      !this.appState.isSaved ||
      (this.masterOrder.orderType === ORDER_TYPES.ADD_ON &&
        AuthContext.model.role !== USER_ROLES.SALES_OPERATIONS_USER) ||
      (this.masterOrder.spiffDiscount.madPoolOverride && AuthContext.model.role !== USER_ROLES.SALES_OPERATIONS_USER) ||
      (this.masterOrder.quoteStatus === QUOTE_STATUS.SIGNED_AND_FINALIZED &&
        AuthContext.model.role !== USER_ROLES.SALES_OPERATIONS_USER) ||
      (this.masterOrder.spiffDiscount.spiffOverrideEnabled &&
        AuthContext.model.role !== USER_ROLES.SALES_OPERATIONS_USER)
    );
  }

  get finalizeBtnErrors() {
    if (
      this.masterOrder.orderType === ORDER_TYPES.ADD_ON &&
      this.masterOrder.quoteStatus === QUOTE_STATUS.FINALIZED &&
      AuthContext.model.role !== USER_ROLES.SALES_OPERATIONS_USER
    ) {
      return 'msg_add_on_can_only_be_un_finalized_by_sales_operation';
    } else if (
      this.finalizeBtnDisabled &&
      ![QUOTE_STATUS.FINALIZED, QUOTE_STATUS.SUBMITTED, QUOTE_STATUS.CLOSED].includes(this.masterOrder.quoteStatus)
    ) {
      const requiredFields = this.requiredFinalizeFields;
      const singleMessage = requiredFields.fields.length === 1 && requiredFields.fields[0].singleMessage;

      return finalizeBtnTooltipText(
        requiredFields,
        AuthContext.model.hasInternalPermissions,
        this.appState.isSaved,
        !this.controller.rollUpDisabled,
        singleMessage
      );
    }

    return '';
  }

  get unFinalizeBtnErrors() {
    if (
      (this.masterOrder.spiffDiscount.madPoolOverride || this.masterOrder.spiffDiscount.spiffOverrideEnabled) &&
      AuthContext.model.role !== USER_ROLES.SALES_OPERATIONS_USER
    ) {
      return 'msg_this_quote_contains_overrides_un_finalization_can_only_be_done_by_sales_operation';
    } else if (
      this.masterOrder.orderType === ORDER_TYPES.ADD_ON &&
      AuthContext.model.role !== USER_ROLES.SALES_OPERATIONS_USER &&
      this.unFinalizeBtnDisabled
    ) {
      return 'msg_add_on_can_only_be_un_finalized_by_sales_operation';
    } else if (this.unFinalizeBtnDisabled) {
      return 'msg_please_save_to_continue';
    } else {
      return '';
    }
  }

  async asyncIsActiveQuote() {
    this.isActiveQuoteValidationRequested = true;
    this.forceRender();

    const isActiveQuote = await Validate.asyncIsActiveQuote(this.masterOrder.id);
    this.isActiveQuoteValidationRequested = false;

    if (!isActiveQuote) {
      this.controller.appState.isEdited = true;

      ErrorContext.model.setProps({
        isShown: true,
        message: 'msg_is_active_quote_validation_failed_message',
        autoHideDelay: 10000,
        showCloseButton: true,
        type: ERROR_CONTEXT_TYPES.WARNING,
      });
    }

    this.forceRender();

    return isActiveQuote;
  }
}

export default ToolbarButtonModel;
